import heic2any from 'heic2any';

const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_QUALITY = 0.85;

const RESIZABLE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'heic',
  'heif',
  'bmp',
  'tiff',
  'tif',
  'avif',
];

const getLowerExtension = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.substring(dot + 1).toLowerCase();
};

const getBaseName = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? name : name.substring(0, dot);
};

export const computeScaledSize = (
  width: number,
  height: number,
  maxDimension: number
): {width: number; height: number} => {
  const scale = Math.min(1, maxDimension / width, maxDimension / height);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

export const isResizableImage = (file: {name: string; type: string}): boolean => {
  const type = file.type.toLowerCase();
  const extension = getLowerExtension(file.name);

  // gif/svg are excluded by MIME or extension (animation / vector).
  if (type === 'image/gif' || type === 'image/svg+xml') {
    return false;
  }
  if (extension === 'gif' || extension === 'svg') {
    return false;
  }

  if (type.startsWith('image/')) {
    return true;
  }
  return RESIZABLE_EXTENSIONS.includes(extension);
};

export const isHeicFile = (file: {name: string; type: string}): boolean => {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
};

const drawToJpeg = (
  imageFile: File,
  originalFile: File,
  maxDimension: number,
  quality: number
): Promise<File> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(imageFile);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Image optimization failed. Please contact support.'));
        return;
      }

      const {width, height} = computeScaledSize(
        img.width,
        img.height,
        maxDimension
      );
      canvas.width = width;
      canvas.height = height;
      // White fill so transparent PNGs don't become black in JPEG.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(
              new Error('Image optimization failed. Please contact support.')
            );
            return;
          }
          if (blob.size >= originalFile.size) {
            resolve(originalFile);
            return;
          }
          resolve(
            new File([blob], getBaseName(originalFile.name) + '.jpg', {
              type: 'image/jpeg',
            })
          );
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error('Failed to load image for resizing. Please contact support.')
      );
    };

    img.src = url;
  });

export const resizeImage = async (
  file: File,
  options?: {maxDimension?: number; quality?: number}
): Promise<File> => {
  const maxDimension = options?.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const quality = options?.quality ?? DEFAULT_QUALITY;

  if (isHeicFile(file)) {
    let convertedBlob: Blob | Blob[];
    try {
      convertedBlob = await heic2any({blob: file, toType: 'image/jpeg'});
    } catch (err) {
      throw new Error(
        'Failed to convert HEIC image. Please contact support. Error: ' +
          (err instanceof Error ? err.message : String(err))
      );
    }
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    const convertedFile = new File([blob], getBaseName(file.name) + '.jpg', {
      type: 'image/jpeg',
    });
    // Compare against the converted JPEG, not the raw HEIC: browsers can't
    // render HEIC, so we must never fall back to the original HEIC bytes.
    return drawToJpeg(convertedFile, convertedFile, maxDimension, quality);
  }

  return drawToJpeg(file, file, maxDimension, quality);
};
